import React, { useState, useEffect, useContext } from 'react';
import styles from './ManagePage.module.css';
import AuthContext from '../../context/AuthContext';
import SingleResultsCard from '../../components/Generator/SingleResultsCard';
import BulkResultsCard from '../../components/Generator/BulkResultsCard';
import ManageHeader from './components/ManageHeader';
import SingleProjectCard from './components/SingleProjectCard';
import BulkProjectCard from './components/BulkProjectCard';
import ListingScoreSection from './components/ListingScoreSection';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import PaginationControls from './components/PaginationControls';
import { useSearch } from './hooks/useSearch';
import { useFilterPanels } from './hooks/useFilterPanels';
import { useFilters } from './hooks/useFilters';
import { useSort } from './hooks/useSort';
import { useProjectTypeFilter } from './hooks/useProjectTypeFilter';
import useManageData from './hooks/useManageData';
import useFilteredProjects from './hooks/useFilteredProjects';
import useProjectSelection from './hooks/useProjectSelection';
import useProjectNameEdit from './hooks/useProjectNameEdit';
import { FaSpinner } from 'react-icons/fa';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  isDraftItem, getListingMode, getCardTitle, getBulkProjectName,
  getCardImage, getBulkPreviewImage, transformGenerationData, formatDate
} from './utils/helpers';
import { makeAuthenticatedRequest } from './utils/apiHelpers';

const ITEMS_PER_PAGE = 20;

const ManagePage = () => {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { project_id } = useParams();

  // Header hooks
  const searchHook = useSearch('');
  const filterPanelsHook = useFilterPanels();
  const filtersHook = useFilters(['draft', 'completed'], []);
  const sortHook = useSort('date-desc');
  const projectTypeFilterHook = useProjectTypeFilter(['single', 'bulk']);

  const { searchTerm } = searchHook;
  const { selectedStatuses, selectedCategories, selectedModes } = filtersHook;
  const { selectedSortOption } = sortHook;
  const { selectedProjectTypes } = projectTypeFilterHook;

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Data
  const data = useManageData({
    token: authContext.token,
    isLoggedIn: authContext.isLoggedIn,
    projectId: project_id,
    navigate,
    location,
    onLogout: () => selection.clearAll(),
  });

  // Filtering
  const { filteredGenerations, filteredBulkGenerations, allFilteredProjects, getUniqueCategories } =
    useFilteredProjects({
      generations: data.generations,
      bulkGenerations: data.bulkGenerations,
      searchTerm, selectedStatuses, selectedCategories, selectedModes, selectedSortOption, selectedProjectTypes,
    });

  // Selection & deletion
  const selection = useProjectSelection({
    token: authContext.token,
    selectedProjectTypes,
    filteredGenerations,
    filteredBulkGenerations,
    allFilteredProjects,
    selectedBulkProject: data.selectedBulkProject,
    setGenerations: data.setGenerations,
    setBulkGenerations: data.setBulkGenerations,
    setSelectedBulkProject: data.setSelectedBulkProject,
    setBulkExcelData: data.setBulkExcelData,
  });

  // Project name editing
  const nameEdit = useProjectNameEdit({
    token: authContext.token,
    setGenerations: data.setGenerations,
    setBulkGenerations: data.setBulkGenerations,
  });

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedStatuses, selectedCategories, selectedModes, selectedSortOption, selectedProjectTypes]);

  // Clear all filters
  const clearAllFilters = () => {
    searchHook.clearSearch();
    filtersHook.clearAllFilters();
    sortHook.setSelectedSortOption('date-desc');
    projectTypeFilterHook.setSelectedProjectTypes(['single', 'bulk']);
  };

  // Draft click handler
  const handleDraftClick = (draft) => {
    if (draft.request_type === 'bulk_draft') {
      navigate('/dashboard/generate/bulk', {
        state: { loadBulkDraft: true, draftData: draft, isNewListing: draft.listing_mode !== 'existing' }
      });
      return;
    }
    navigate('/dashboard/generate/single', {
      state: { loadDraft: true, draftData: draft, isNewListing: draft.listing_mode !== 'existing' }
    });
  };

  // Card click handlers
  const handleCardClick = (generation) => {
    if (selection.isSelectionMode) return;
    if (isDraftItem(generation)) { handleDraftClick(generation); return; }
    data.setSelectedGeneration(transformGenerationData(generation));
    data.setShowResults(true);
  };

  const handleBulkCardClick = async (bulkProject) => {
    if (selection.isBulkSelectionMode) return;
    if (isDraftItem(bulkProject)) { handleDraftClick(bulkProject); return; }
    if (bulkProject.image_urls) {
      data.loadBulkExcelData(bulkProject);
      data.setSelectedBulkProject(bulkProject);
      data.setShowBulkResults(true);
      return;
    }
    // Slim mode: fetch full project data
    try {
      const url = `${process.env.REACT_APP_API_URL}/auth/user_history/${bulkProject._id}`;
      const response = await makeAuthenticatedRequest(url, authContext.token);
      if (response.data?.data) {
        const fullProject = response.data.data;
        data.setBulkGenerations(prev => prev.map(p => p._id === fullProject._id ? fullProject : p));
        data.loadBulkExcelData(fullProject);
        data.setSelectedBulkProject(fullProject);
      } else {
        data.setSelectedBulkProject(bulkProject);
      }
    } catch {
      data.setSelectedBulkProject(bulkProject);
    }
    data.setShowBulkResults(true);
  };

  const handleCloseResults = () => { data.setShowResults(false); data.setSelectedGeneration(null); };
  const handleCloseBulkResults = () => {
    data.setShowBulkResults(false); data.setSelectedBulkProject(null); data.setBulkExcelData(null);
  };

  // Pagination
  const getPaginatedData = (items) => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return items.slice(start, start + ITEMS_PER_PAGE);
  };

  // Computed
  const isInSelectionMode = (type) => {
    if (type === 'single') return selection.isSelectionMode || (selection.isBulkSelectionMode && selectedProjectTypes.includes('single') && selectedProjectTypes.includes('bulk'));
    return selection.isBulkSelectionMode || (selection.isSelectionMode && selectedProjectTypes.includes('single') && selectedProjectTypes.includes('bulk'));
  };

  // --- Render ---

  if (data.loading) {
    return <div className={styles.container}><div className={styles.loading}><FaSpinner className={styles.spinner} /></div></div>;
  }
  if (data.error) {
    return <div className={styles.container}><div className={styles.error}>{data.error}</div></div>;
  }
  if (data.showBulkResults && data.selectedBulkProject) {
    return (
      <div className={styles.container}>
        <BulkResultsCard onClose={handleCloseBulkResults} bulkProjectData={data.selectedBulkProject}
          preTransformedData={data.bulkExcelData} isDataLoading={data.bulkDataLoading} />
      </div>
    );
  }
  if (data.showResults && data.selectedGeneration) {
    return (
      <div className={styles.container}>
        <SingleResultsCard resultData={data.selectedGeneration} onClose={handleCloseResults}
          isInline={true} showFormButton={false} onContentUpdated={data.handleContentUpdated}
          authToken={authContext.token} />
      </div>
    );
  }

  const totalSelected = selection.selectedProjects.length + selection.selectedBulkProjects.length;
  const showProjectsList = selectedProjectTypes.includes('single') || selectedProjectTypes.includes('bulk');
  const hasNoProjects = data.generations.length === 0 && data.bulkGenerations.length === 0;
  const hasNoFilteredProjects = allFilteredProjects.length === 0;

  return (
    <>
      {selection.showDeleteConfirm && (
        <DeleteConfirmModal count={totalSelected} isDeleting={selection.isDeleting}
          onCancel={() => selection.setShowDeleteConfirm(false)} onConfirm={selection.handleConfirmedDelete} />
      )}

      <div className={styles.container}>
        <ManageHeader searchHook={searchHook} filterPanelsHook={filterPanelsHook}
          filtersHook={filtersHook} sortHook={sortHook}
          projectTypeFilterHook={projectTypeFilterHook} getUniqueCategories={getUniqueCategories} />

        {selectedProjectTypes.length === 1 && selectedProjectTypes.includes('listing_score') && (
          <ListingScoreSection token={authContext.token} />
        )}

        {showProjectsList && (hasNoProjects || hasNoFilteredProjects) && (
          <div className={styles.emptyState}>
            {hasNoProjects ? (
              <>
                <h3>No projects found</h3>
                <p>You haven't created any projects yet. Create your first project to see it here.</p>
              </>
            ) : (
              <>
                <h3>No projects match your filters</h3>
                <p>Try adjusting your search terms or filters to see more results.</p>
                <button onClick={clearAllFilters} className={styles.clearFiltersButton}>Clear All Filters</button>
              </>
            )}
          </div>
        )}

        {showProjectsList && !hasNoProjects && !hasNoFilteredProjects && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              {(selection.isSelectionMode || selection.isBulkSelectionMode) ? (
                <>
                  <div className={styles.selectionHeaderLeft}>
                    <span className={styles.selectedCountText}>({totalSelected}) Selected</span>
                    <button type="button" className={styles.selectedBadge} onClick={selection.handleCloseSelection}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.5 4.1125L9.8875 3.5L7 6.3875L4.1125 3.5L3.5 4.1125L6.3875 7L3.5 9.8875L4.1125 10.5L7 7.6125L9.8875 10.5L10.5 9.8875L7.6125 7L10.5 4.1125Z" fill="#505050"/>
                      </svg>
                    </button>
                  </div>
                  <div className={styles.selectionHeaderRight}>
                    <label className={styles.selectAllLinkButton}>
                      <input type="checkbox" className={styles.selectAllCheckbox}
                        checked={selection.isAllSelected} onChange={selection.handleSelectAllClick} />
                      <span className={styles.selectAllText}>Select All ({allFilteredProjects.length})</span>
                    </label>
                    <button className={styles.deleteButtonRed} onClick={selection.handleDeleteSelected}>
                      <svg width="20" height="23" viewBox="0 0 20 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18.982 4.53334H16.2461L15.7032 2.36069C15.3558 0.969984 14.1117 0 12.6797 0H7.15306C5.72093 0 4.47791 0.969984 4.12948 2.36069L3.58658 4.53334H0.849933C0.380335 4.53334 0 4.91367 0 5.38327C0 5.85287 0.380335 6.2332 0.849933 6.2332H1.19203L2.11738 19.2041C2.23318 20.8264 3.59838 22.0982 5.22609 22.0982H14.6039C16.2315 22.0982 17.5968 20.8265 17.7126 19.2041L18.638 6.2332H18.9801C19.4497 6.2332 19.83 5.85287 19.83 5.38327C19.83 4.91367 19.4497 4.53334 18.9801 4.53334H18.982ZM5.77933 2.7729C5.93762 2.14077 6.50284 1.69987 7.15409 1.69987H12.6807C13.3319 1.69987 13.8972 2.14183 14.0555 2.7729L14.4953 4.53334H5.33834L5.77818 2.7729H5.77933ZM16.0188 19.0832C15.9667 19.8205 15.3452 20.3984 14.6058 20.3984H5.22686C4.48634 20.3984 3.8659 19.8205 3.81385 19.0832L2.89592 6.23328H16.9357L16.0178 19.0832H16.0188ZM11.3326 15.5825V9.91669C11.3326 9.44709 11.7129 9.06675 12.1825 9.06675C12.6521 9.06675 13.0324 9.44709 13.0324 9.91669V15.5825C13.0324 16.0521 12.6521 16.4325 12.1825 16.4325C11.7129 16.4325 11.3326 16.0521 11.3326 15.5825ZM6.80033 15.5825V9.91669C6.80033 9.44709 7.18067 9.06675 7.65026 9.06675C8.11986 9.06675 8.5002 9.44709 8.5002 9.91669V15.5825C8.5002 16.0521 8.11986 16.4325 7.65026 16.4325C7.18067 16.4325 6.80033 16.0521 6.80033 15.5825Z" fill="white"/>
                      </svg>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.sectionHeaderTitle}>
                    <span className={styles.allProjectsTitle}>All Projects ({allFilteredProjects.length})</span>
                  </div>
                  <div className={styles.sectionHeaderActions}>
                    <button className={styles.selectLinkButton} onClick={selection.handleEnableSelection}>Select</button>
                  </div>
                </>
              )}
            </div>

            <div className={styles.smallCardsGrid}>
              {getPaginatedData(allFilteredProjects).map((project) => {
                if (project.projectType === 'single') {
                  const generationId = project._id;
                  const currentProjectName = getCardTitle(project);
                  return (
                    <SingleProjectCard
                      key={`all-${generationId}`}
                      generation={project}
                      cardImage={getCardImage(project)}
                      currentProjectName={currentProjectName}
                      isEditing={nameEdit.editingProjectName === generationId}
                      isDraft={isDraftItem(project)}
                      isSelected={selection.selectedProjects.includes(generationId)}
                      listingMode={getListingMode(project)}
                      editingProjectName={nameEdit.editingProjectName}
                      newProjectName={nameEdit.newProjectName}
                      setNewProjectName={nameEdit.setNewProjectName}
                      onSaveProjectName={nameEdit.handleSaveProjectName}
                      onCancelEdit={nameEdit.handleCancelEdit}
                      onStartEdit={() => { nameEdit.setEditingProjectName(generationId); nameEdit.setNewProjectName(currentProjectName); }}
                      onCardClick={() => handleCardClick(project)}
                      onSelectionChange={selection.handleProjectSelection}
                      isSelectionMode={isInSelectionMode('single')}
                      formatDate={formatDate}
                    />
                  );
                }
                const projectName = getBulkProjectName(project);
                return (
                  <BulkProjectCard
                    key={`all-bulk-${project._id}`}
                    bulkProject={project}
                    previewImage={getBulkPreviewImage(project)}
                    projectName={projectName}
                    isSelected={selection.selectedBulkProjects.includes(project._id)}
                    listingMode={getListingMode(project)}
                    editingBulkProjectName={nameEdit.editingBulkProjectName}
                    newBulkProjectName={nameEdit.newBulkProjectName}
                    setNewBulkProjectName={nameEdit.setNewBulkProjectName}
                    onSaveBulkProjectName={nameEdit.handleSaveBulkProjectName}
                    onCancelBulkProjectNameEdit={nameEdit.handleCancelBulkProjectNameEdit}
                    onStartBulkEdit={() => { nameEdit.setEditingBulkProjectName(project._id); nameEdit.setNewBulkProjectName(projectName); }}
                    isUpdatingBulkProjectName={nameEdit.isUpdatingBulkProjectName}
                    onCardClick={() => handleBulkCardClick(project)}
                    onSelectionChange={selection.handleBulkProjectSelection}
                    isSelectionMode={isInSelectionMode('bulk')}
                    formatDate={formatDate}
                  />
                );
              })}
            </div>

            <PaginationControls totalItems={allFilteredProjects.length} currentPage={currentPage}
              itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
          </div>
        )}

        {showProjectsList && data.bulkLoading && (
          <div className={styles.loading}><FaSpinner className={styles.spinner} /></div>
        )}
        {showProjectsList && data.bulkError && !data.bulkLoading && (
          <div className={styles.error}>{data.bulkError}</div>
        )}
      </div>
    </>
  );
};

export default ManagePage;
